import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TtsVoice, VOCAL_RANGE_DESC } from '../../types/tts'
import { tokens } from '../../theme/tokens'

interface VoiceCardProps {
  voice: TtsVoice
  onSelect: (voiceId: string) => void
  onDelete?: (voiceId: string) => void
  onPreview: (voiceId: string, previewUrl?: string) => void
  isPlaying?: boolean
}

const VoiceCard: React.FC<VoiceCardProps> = ({
  voice,
  onSelect,
  onDelete,
  onPreview,
  isPlaying = false,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isCloned = voice.type === 'cloned'
  const rangeDesc = voice.range ? VOCAL_RANGE_DESC[voice.range] : undefined

  const handleTryIt = () => {
    navigate(`/speech/tts?voice=${encodeURIComponent(voice.voice_id)}`)
  }

  const handlePreview = () => {
    onPreview(voice.voice_id, voice.preview_url)
  }

  const handleDelete = () => {
    if (onDelete) onDelete(voice.voice_id)
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        {/* Lead with the human name */}
        <Typography variant="h6" gutterBottom noWrap title={voice.displayName}>
          {voice.displayName}
        </Typography>

        {/* Range (with human descriptor) + gender — the catalog-v4 voice identity */}
        {(voice.range || (voice.gender && voice.gender !== 'unknown')) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {voice.range && (
              <Box component="span" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                {voice.range}
              </Box>
            )}
            {voice.range && rangeDesc ? ` · ${rangeDesc}` : ''}
            {voice.range && voice.gender && voice.gender !== 'unknown' ? ' · ' : ''}
            {voice.gender && voice.gender !== 'unknown' && (
              <Box component="span" sx={{ textTransform: 'capitalize' }}>
                {voice.gender}
              </Box>
            )}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {voice.language && voice.language !== 'unknown' && (
            <Chip
              label={voice.language.toUpperCase()}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <Chip
            label={isCloned ? t('cloned', 'Cloned') : t('stock', 'Stock')}
            size="small"
            sx={{
              backgroundColor: isCloned ? tokens.colors.tts.main : tokens.colors.primary.darkText,
              color: '#fff',
              fontWeight: 700,
            }}
          />
        </Box>

        {/* Cloned voices keep the user's own description; stock voices intentionally
            never surface a source corpus. */}
        {isCloned && voice.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {voice.description}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 1.5, pb: 1.5 }}>
        <Box>
          <Tooltip title={isPlaying ? t('stopPreview', 'Stop') : t('previewVoice', 'Preview')}>
            <span>
              <IconButton
                size="small"
                onClick={handlePreview}
                disabled={!voice.preview_url}
                sx={{ color: isPlaying ? tokens.colors.primary.darkText : undefined }}
              >
                {isPlaying ? <StopIcon /> : <PlayArrowIcon />}
              </IconButton>
            </span>
          </Tooltip>

          {isCloned && onDelete && (
            <Tooltip title={t('deleteVoice', 'Delete voice')}>
              <IconButton size="small" onClick={handleDelete} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Button
          size="small"
          variant="contained"
          onClick={handleTryIt}
          sx={{
            background: tokens.gradients.tts,
            '&:hover': { background: `linear-gradient(135deg, ${tokens.colors.tts.light} 0%, ${tokens.colors.tts.main} 100%)` },
            textTransform: 'none',
          }}
        >
          {t('tryIt', 'Try it')}
        </Button>
      </CardActions>
    </Card>
  )
}

export default VoiceCard
